import styled from '@emotion/styled'
import { Form } from 'react-bootstrap'
import theme from '../constants/theme'

const PAGE_BG = '#F2F5F9'
const BORDER = '#E7EBF0'
const HEADING = '#111827'
const MUTED = '#6B7280'
const FAINT = '#9CA3AF'

// ── Page shell ────────────────────────────────────────────────────────────────

export const OfferPage = styled('div')({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: PAGE_BG,
})

export const OfferScroll = styled('div')({
  flexGrow: 1,
  overflowY: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
})

export const OfferContent = styled('div')({
  width: '100%',
  maxWidth: '920px',
  margin: '0 auto',
  padding: '18px 16px 40px',
})

export const TopBar = styled('div')({
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  backgroundColor: theme.white,
  borderBottom: `1px solid ${BORDER}`,
})

export const TopBarInner = styled('div')({
  width: '100%',
  maxWidth: '920px',
  margin: '0 auto',
  padding: '10px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
})

// ── Hero banner ───────────────────────────────────────────────────────────────

export const HeroBanner = styled('div')({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '16px',
  padding: '26px 28px 24px',
  background: `linear-gradient(135deg, #062F46 0%, ${theme.talentinoSpecialDarkBlue} 60%, ${theme.talentinoSpecialBlue} 100%)`,
  boxShadow: '0 6px 22px rgba(6, 47, 70, 0.18)',
})

export const HeroRing = styled('div')({
  position: 'absolute',
  top: '-40px',
  right: '-30px',
  borderRadius: '50%',
  pointerEvents: 'none',
})

export const HeroInner = styled('div')({
  position: 'relative',
  zIndex: 1,
})

export const HeroBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 12px',
  borderRadius: '50px',
  backgroundColor: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.22)',
  color: 'rgba(255,255,255,0.92)',
  fontSize: '10px',
  fontWeight: '600',
  letterSpacing: '1px',
  textTransform: 'uppercase',
})

export const HeroTitle = styled('h1')({
  margin: '14px 0 0',
  color: theme.white,
  fontSize: '30px',
  fontWeight: '700',
  lineHeight: 1.2,
  wordBreak: 'break-word',
})

export const HeroMetaRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '10px 22px',
  marginTop: '14px',
})

export const HeroMetaItem = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '7px',
  color: 'rgba(255,255,255,0.78)',
  fontSize: '13px',
})

// ── Status strip + countdown ───────────────────────────────────────────────────

export const StatusCard = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '14px',
  marginTop: '16px',
  padding: '14px 18px',
  backgroundColor: theme.white,
  border: `1px solid ${BORDER}`,
  borderLeft: `3px solid ${theme.talentinoBlue}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
})

export const StatusLeft = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '11px',
  minWidth: 0,
})

export const StatusTitle = styled('p')({
  margin: '0 0 2px',
  fontSize: '13px',
  fontWeight: '600',
  color: HEADING,
})

export const StatusSubtitle = styled('p')({
  margin: 0,
  fontSize: '12px',
  color: MUTED,
})

export const CountdownRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '18px',
  flexShrink: 0,
})

export const CountdownUnit = styled('div')({
  textAlign: 'center',
  minWidth: '38px',
})

export const CountdownValue = styled('div')({
  fontSize: '19px',
  fontWeight: '700',
  color: theme.talentinoSpecialBlue,
  lineHeight: 1,
})

export const CountdownLabel = styled('div')({
  marginTop: '3px',
  fontSize: '9px',
  fontWeight: '600',
  color: FAINT,
  letterSpacing: '1px',
  textTransform: 'uppercase',
})

// ── Generic card ──────────────────────────────────────────────────────────────

export const SectionCard = styled('div')({
  height: '100%',
  padding: '20px',
  backgroundColor: theme.white,
  border: `1px solid ${BORDER}`,
  borderRadius: '14px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
})

export const CardHeadingRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '14px',
})

export const CardHeading = styled('h2')({
  margin: 0,
  fontSize: '14px',
  fontWeight: '600',
  color: HEADING,
})

export const SoftDivider = styled('hr')({
  margin: '18px 0',
  border: 0,
  borderTop: `1px solid ${BORDER}`,
  opacity: 1,
})

const PILL_TONES = {
  success: { bg: '#ECFDF3', fg: theme.talentinoGreen, border: '#BBEBD1' },
  warning: { bg: '#FFF8E1', fg: '#92400E', border: '#FFE082' },
  info: { bg: '#EFF6FF', fg: theme.talentinoSpecialBlue, border: '#CFE3F5' },
}

export const StatusPill = styled('span')(({ tone = 'info' }) => {
  const { bg, fg, border } = PILL_TONES[tone] ?? PILL_TONES.info
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '50px',
    backgroundColor: bg,
    border: `1px solid ${border}`,
    color: fg,
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  }
})

// ── Offer letter body ─────────────────────────────────────────────────────────

export const GreetingRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

export const AvatarCircle = styled('div')({
  width: '38px',
  height: '38px',
  borderRadius: '50%',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#EEF3F8',
  color: theme.talentinoSpecialBlue,
  fontSize: '13px',
  fontWeight: '700',
  letterSpacing: '0.5px',
})

export const GreetingName = styled('p')({
  margin: '0 0 2px',
  fontSize: '14px',
  fontWeight: '600',
  color: HEADING,
})

export const GreetingFrom = styled('p')({
  margin: 0,
  fontSize: '12px',
  color: FAINT,
})

export const LetterBody = styled('p')({
  margin: '16px 0 0',
  fontSize: '13.5px',
  lineHeight: 1.9,
  color: theme.talentinoTextGray,
})

export const DetailsGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '18px 20px',
})

export const DetailLabel = styled('div')({
  fontSize: '9.5px',
  fontWeight: '600',
  color: FAINT,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  marginBottom: '5px',
})

export const DetailValue = styled('div')({
  fontSize: '13px',
  fontWeight: '600',
  color: '#1F2937',
  wordBreak: 'break-word',
})

// ── Attachment ────────────────────────────────────────────────────────────────

export const AttachmentBox = styled('div')({
  padding: '14px',
  borderRadius: '12px',
  border: `1px solid ${BORDER}`,
  backgroundColor: '#F8FAFC',
})

export const AttachmentTopRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minWidth: 0,
})

export const FileIconWrapper = styled('div')(({ accentcolor }) => ({
  width: '38px',
  height: '38px',
  borderRadius: '9px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.white,
  border: `1px solid ${accentcolor}22`,
}))

export const FileName = styled('div')({
  fontSize: '13px',
  fontWeight: '600',
  color: HEADING,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const FileMeta = styled('div')({
  marginTop: '2px',
  fontSize: '11px',
  color: FAINT,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const ViewLink = styled('a')(({ accentcolor }) => ({
  marginTop: '12px',
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '7px',
  padding: '9px 14px',
  borderRadius: '50px',
  border: `1.5px solid ${accentcolor}`,
  backgroundColor: theme.white,
  color: accentcolor,
  fontSize: '13px',
  fontWeight: '600',
  textDecoration: 'none',
  transition: 'background-color 0.15s, color 0.15s',
  '&:hover': {
    backgroundColor: accentcolor,
    color: theme.white,
  },
}))

// ── Notes / inline notices ────────────────────────────────────────────────────

export const NoteBox = styled('div')(({ accentcolor, tint }) => ({
  padding: '13px 15px',
  borderRadius: '10px',
  backgroundColor: tint,
  border: `1px solid ${accentcolor}33`,
  borderLeft: `3px solid ${accentcolor}`,
}))

export const NoteLabel = styled('div')(({ accentcolor }) => ({
  marginBottom: '4px',
  fontSize: '10px',
  fontWeight: '600',
  color: accentcolor,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
}))

export const NoteText = styled('p')(({ color }) => ({
  margin: 0,
  fontSize: '12.5px',
  lineHeight: 1.7,
  color,
}))

// ── Decision actions ──────────────────────────────────────────────────────────

export const DecisionRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
})

const ACTION_TONES = {
  primary: {
    bg: theme.talentinoSpecialBlue,
    fg: theme.white,
    border: theme.talentinoSpecialBlue,
    hoverBg: theme.talentinoSpecialDarkBlue,
    hoverFg: theme.white,
  },
  danger: {
    bg: theme.white,
    fg: theme.talentinoRed,
    border: '#E9D2D1',
    hoverBg: '#FDF2F2',
    hoverFg: theme.talentinoDarkerRed,
  },
  neutral: {
    bg: theme.white,
    fg: MUTED,
    border: BORDER,
    hoverBg: '#F3F6F9',
    hoverFg: HEADING,
  },
  violet: {
    bg: theme.white,
    fg: '#7c3aed',
    border: '#DDD3F7',
    hoverBg: '#F5F3FF',
    hoverFg: '#6d28d9',
  },
  accept: {
    bg: theme.talentinoGreen,
    fg: theme.white,
    border: theme.talentinoGreen,
    hoverBg: theme.talentinoDarkerGreen,
    hoverFg: theme.white,
  },
  decline: {
    bg: theme.talentinoDarkerRed,
    fg: theme.white,
    border: theme.talentinoDarkerRed,
    hoverBg: theme.talentinoRed,
    hoverFg: theme.white,
  },
  submitViolet: {
    bg: '#7c3aed',
    fg: theme.white,
    border: '#7c3aed',
    hoverBg: '#6d28d9',
    hoverFg: theme.white,
  },
}

export const ActionButton = styled('button')(({ tone = 'primary' }) => {
  const { bg, fg, border, hoverBg, hoverFg } =
    ACTION_TONES[tone] ?? ACTION_TONES.primary
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    padding: '10px 22px',
    borderRadius: '50px',
    border: `1.5px solid ${border}`,
    backgroundColor: bg,
    color: fg,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.15s, color 0.15s, opacity 0.15s',
    '&:hover:not(:disabled)': { backgroundColor: hoverBg, color: hoverFg },
    '&:disabled': { opacity: 0.55, cursor: 'not-allowed' },
  }
})

export const LinkButton = styled('button')({
  padding: 0,
  border: 0,
  background: 'none',
  color: '#7c3aed',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
})

// ── Flow panels (accept / decline / negotiate) ────────────────────────────────

export const FlowPanel = styled('div')(({ accentcolor, tint }) => ({
  padding: '18px',
  borderRadius: '12px',
  backgroundColor: tint,
  border: `1px solid ${accentcolor}33`,
}))

export const FlowTitle = styled('p')(({ color }) => ({
  margin: '0 0 4px',
  fontSize: '13px',
  fontWeight: '600',
  color,
}))

export const FlowHelp = styled('p')({
  margin: 0,
  fontSize: '12px',
  lineHeight: 1.7,
  color: theme.talentinoTextGray,
})

export const FlowActions = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  marginTop: '4px',
})

export const FieldLabel = styled(Form.Label)({
  fontSize: '11px',
  fontWeight: '600',
  color: MUTED,
  letterSpacing: '0.3px',
})

export const RequiredMark = styled('span')({
  marginLeft: '3px',
  color: theme.talentinoRed,
})

export const FieldInput = styled(Form.Control)({
  fontSize: '13px',
  padding: '9px 12px',
  borderRadius: '9px',
  border: `1px solid ${theme.defaultDisabledBorderColor}`,
  backgroundColor: theme.white,
  resize: 'none',
  '&:focus': {
    borderColor: theme.talentinoBlue,
    boxShadow: `0 0 0 3px ${theme.talentinoBlue}1F`,
  },
})

export const FieldHint = styled('div')({
  marginTop: '5px',
  fontSize: '11px',
  color: FAINT,
})

export const FieldError = styled('div')({
  marginTop: '5px',
  fontSize: '11px',
  color: theme.talentinoRed,
})

export const ProposedFieldsScroll = styled('div')({
  maxHeight: '320px',
  overflowY: 'auto',
  paddingRight: '4px',
  marginBottom: '4px',
})

// ── Footer ────────────────────────────────────────────────────────────────────

export const SecureFooter = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '7px',
  marginTop: '20px',
  color: FAINT,
  fontSize: '11.5px',
  textAlign: 'center',
})
